import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { orgPath, workspacePath } from '../../nav';
import { useWorkspace } from '../Workspace';

interface OrgSummary {
  id: string;
  label: string;
  environment: string;
  status: string;
}

/**
 * The workspace's front page: the Salesforce orgs it connects to.
 *
 * This is the step people said they could not find - from the company to the environments it works in -
 * so every org is a tile with its own way in, rather than a name in a list whose click target was the
 * only clue it led anywhere. Production and sandboxes are split under their own headings, because acting
 * in the wrong one is the mistake that matters and two orgs with the same label are otherwise easy to mix up.
 */
export function WorkspaceOrgs() {
  const { tenantId, canManage } = useWorkspace();
  const [orgs, setOrgs] = useState<OrgSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const o = await api<{ orgs: OrgSummary[] }>(`/api/tenants/${tenantId}/orgs`);
      setOrgs(o.orgs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the orgs here');
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!orgs) return error ? <div className="alert">{error}</div> : <p className="muted">Loading…</p>;

  // Anything that is not a sandbox is treated as production, the same rule the env-tag follows, so an org
  // can never fall between the two headings.
  const groups = [
    { title: 'Production', orgs: orgs.filter((o) => o.environment !== 'sandbox') },
    { title: 'Sandboxes', orgs: orgs.filter((o) => o.environment === 'sandbox') },
  ].filter((g) => g.orgs.length > 0);

  return (
    <>
      {error && <div className="alert">{error}</div>}
      {canManage && (
        <div className="page-actions" style={{ marginBottom: 16 }}>
          <Link className="button" to={workspacePath(tenantId, 'orgs/new')}>
            Connect an org
          </Link>
        </div>
      )}

      {orgs.length === 0 &&
        (canManage ? (
          <div className="card">
            <h3>No Salesforce orgs connected yet</h3>
            <p className="muted" style={{ margin: 0 }}>
              Connecting an org installs nothing by itself. You will be walked through installing the Kruvi
              Access package there first, then signing in as an admin to authorise it.
            </p>
          </div>
        ) : (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              You do not hold access to any Salesforce org here. An owner or admin of this workspace can grant it.
            </p>
          </div>
        ))}

      {groups.map((g, i) => (
        <section key={g.title}>
          <h2 style={i === 0 ? { marginTop: 0 } : undefined}>{g.title}</h2>
          <div className="tiles">
            {g.orgs.map((o) => (
              <div key={o.id} className="tile">
                <div className="tile-title">
                  <Link to={orgPath(tenantId, o.id)}>{o.label}</Link>
                </div>
                <div className="tile-meta">
                  <span className={`env-tag ${o.environment}`}>
                    {o.environment === 'sandbox' ? 'Sandbox' : 'Production'}
                  </span>
                  <span className={`pill ${o.status === 'active' ? 'on' : o.status === 'pending' ? '' : 'off'}`}>
                    {o.status}
                  </span>
                </div>
                {/* Secondary, every one of them: a page of tiles each with a solid button reads as a dozen
                    competing main actions, and the one thing to do here is connect an org. */}
                <div className="actions">
                  <Link className="button secondary" to={orgPath(tenantId, o.id)}>
                    {canManage ? 'Manage' : 'View'}
                  </Link>
                  {canManage && (
                    <Link className="button secondary" to={orgPath(tenantId, o.id, 'grants')}>
                      Access grants
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
